import i18n from '@/i18n'
import type { MotorContract } from '@/types/motor/MotorContract'
import type { EMobilContract } from '@/types/emobil/EMobilContract'
import { isEMobilContract } from '@/types/emobil/EMobilContract'
import type { Policy } from 'policies-api'
import type { MotorProductType, TileHeader } from '@/types/motor/MotorEnum'
import { startsWithOneOf } from '@/utils/CommonUtils'
import { isTechInsuranceNonEBikeContract } from '@/utils/motor/TechInsuranceUtils'

// better use locales in the future
const MotorProductTypeRecord: Record<MotorProductType, TileHeader> = {
  ANHAENGER: {
    icon: 'icon-wohnwagen_outline',
    title: 'Anhänger-Versicherung'
  },
  AUTO: {
    icon: 'icon-auto',
    title: 'Kfz-Versicherung'
  },
  E_BIKE: {
    icon: 'icon-eMobilschutz_bike',
    title: 'E-Bike Versicherung'
  },
  E_MOPED: {
    icon: 'icon-eMobilschutz_Scooter',
    title: 'E-Scooter/Moped Versicherung'
  },
  FALLBACK: {
    icon: 'icon-vertraege',
    title: 'Kfz-Versicherung'
  },
  MOTORRAD: {
    icon: 'icon-motorad',
    title: 'Motorradversicherung'
  },
  OLDTIMER: {
    icon: 'icon-Oldtimer',
    title: 'Oldtimer-Versicherung'
  },
  QUAD: {
    icon: 'icon-quad',
    title: 'Quad-Versicherung'
  },
  ROLLER: {
    icon: 'icon-roller',
    title: 'Rollerversicherung'
  },
  SEASONAL_ANHAENGER: {
    icon: 'icon-Saison_allgemein',
    title: 'Anhänger-Versicherung'
  },
  SEASONAL_FALLBACK: {
    icon: 'icon-Saison_allgemein',
    title: 'Saison-Versicherung'
  },
  SEASONAL_KFZ: {
    icon: 'icon-Saison_Kfz',
    title: 'Kfz-Versicherung'
  },
  SEASONAL_MOTORRAD: {
    icon: 'icon-Saison_Motorrad',
    title: 'Motorradversicherung'
  },
  SEASONAL_QUAD: {
    icon: 'icon-Saison_allgemein',
    title: 'Quad-Versicherung'
  },
  SEASONAL_ROLLER: {
    icon: 'icon-Saison_scooter',
    title: 'Rollerversicherung'
  },
  SEASONAL_TRIKE: {
    icon: 'icon-Saison_allgemein',
    title: 'Trike-Versicherung'
  },
  SEASONAL_WOHNMOBIL: {
    icon: 'icon-Saison_Womo',
    title: 'Wohnmobilversicherung'
  },
  TECH_INSURANCE: {
    icon: 'icon-weitere_VS',
    title: 'Sonstige Absicherung'
  },
  TRIKE: {
    icon: 'icon-trike',
    title: 'Trike-Versicherung'
  },
  WOHNMOBIL: {
    icon: 'icon-wohnmobil_outline',
    title: 'Wohnmobilversicherung'
  }
}

export function swapLettersAndNumbers(input: string): string | null {
  const regex = /^([A-Z]{3})(\d{3})$/
  const match = regex.exec(input)

  if (match) {
    const letters = match[1]
    const numbers = match[2]
    return `${numbers} ${letters}`
  }

  return null // If the String is not matching
}

export const resolveMotorIconString = resolveMotorProductInfo('Motor', (r) => r.icon)

export const resolveMotorTitle = resolveMotorProductInfo(
  i18n.global.t('motorPolicyTile.insuranceType.carInsurance').toString(),
  (r) => r.title
)

export function getVehicleTypeName(contract: MotorContract | EMobilContract): string {
  if (isEMobilContract(contract)) {
    return 'EBIKE'
  }
  if (!contract?.vehicleType?.code) return ''
  const wkz = contract.vehicleType.code // wagniskennziffer
  if (isAuto(wkz)) {
    return 'AUTO'
  } else if (isMotorrad(wkz)) {
    return 'MOTORRAD'
  } else if (isWohnmobil(wkz)) {
    return 'WOHNMOBIL'
  } else if (isAnhaenger(wkz)) {
    return 'ANHAENGER'
  } else if (isRoller(wkz)) {
    return 'ROLLER'
  } else if (isTrike(wkz)) {
    return 'TRIKE'
  } else if (isQuad(wkz)) {
    return 'QUAD'
  } else if (isEMoped(wkz)) {
    return 'E_MOPED'
  }
  return 'FALLBACK'
}

function getBaseVehicleType(wkz: string): MotorProductType | null {
  if (isAuto(wkz)) return 'AUTO'
  if (isMotorrad(wkz)) return 'MOTORRAD'
  if (isWohnmobil(wkz)) return 'WOHNMOBIL'
  if (isAnhaenger(wkz)) return 'ANHAENGER'
  if (isRoller(wkz)) return 'ROLLER'
  if (isTrike(wkz)) return 'TRIKE'
  if (isQuad(wkz)) return 'QUAD'
  if (isEMoped(wkz)) return 'E_MOPED'
  return null
}

function getSeasonalVehicleType(baseType: MotorProductType): MotorProductType {
  const seasonalMap: Record<string, MotorProductType> = {
    AUTO: 'SEASONAL_KFZ',
    MOTORRAD: 'SEASONAL_MOTORRAD',
    WOHNMOBIL: 'SEASONAL_WOHNMOBIL',
    ANHAENGER: 'SEASONAL_ANHAENGER',
    ROLLER: 'SEASONAL_ROLLER',
    TRIKE: 'SEASONAL_TRIKE',
    QUAD: 'SEASONAL_QUAD'
  }
  return seasonalMap[baseType] || baseType
}

function handleSpecialCases(
  contract?: MotorContract | EMobilContract
): MotorProductType | null {
  if (isEMobilContract(contract)) {
    return 'E_BIKE'
  }

  if (contract?.insuredVehicle?.fleetNumber === '00009003') {
    return 'OLDTIMER'
  }

  return null
}

function resolveMotorProductInfo(
  fallback: string,
  extractor: (header: TileHeader) => string
) {
  return (contract?: MotorContract | EMobilContract, policy?: Policy) => {
    const get = (pt: MotorProductType) => extractor(MotorProductTypeRecord[pt])

    if (isTechInsuranceNonEBikeContract(contract ?? null, policy)) {
      return get('TECH_INSURANCE')
    }

    // Handle special cases first
    const specialCase = handleSpecialCases(contract)
    if (specialCase) {
      return get(specialCase)
    }

    // Type guard: only MotorContract has vehicleType property
    if (!contract || isEMobilContract(contract)) {
      return fallback
    }

    const wkz = contract.vehicleType?.code
    if (!wkz) {
      return fallback
    }

    const baseType = getBaseVehicleType(wkz)
    if (!baseType) {
      const isSeasonal = Boolean(contract.insuredVehicle?.seasonalPeriod)
      if (isSeasonal) {
        return fallback === 'Motor' ? get('SEASONAL_FALLBACK') : fallback
      }
      // Unbekannter WKZ ohne Saison-Vertrag → generischer FALLBACK
      // (statt des rohen fallback-Strings, der z.B. über IconClassMap
      // fälschlich zu 'icon-auto' aufgelöst wurde, siehe Bug mit wkz 30101)
      return get('FALLBACK')
    }

    // E_MOPED doesn't have seasonal variant
    if (baseType === 'E_MOPED') {
      return get(baseType)
    }

    const isSeasonal = Boolean(contract.insuredVehicle?.seasonalPeriod)
    const finalType = isSeasonal ? getSeasonalVehicleType(baseType) : baseType

    return get(finalType)
  }
}

export function isAuto(wkz: string) {
  return startsWithOneOf(wkz, ['112'])
}

export function isKrad(wkz: string): boolean {
  return startsWithOneOf(wkz, ['003', '012', '016', '018', '026', '028', '030'])
}

function isMotorrad(wkz: string) {
  return startsWithOneOf(wkz, ['003', '026', '028', '766', '768', '776', '778'])
}

function isWohnmobil(wkz: string) {
  return startsWithOneOf(wkz, ['127'])
}

function isAnhaenger(wkz: string) {
  return startsWithOneOf(wkz, [
    '501',
    '502',
    '505',
    '511',
    '515',
    '522',
    '541',
    '551',
    '581',
    '977'
  ])
}

function isRoller(wkz: string) {
  return startsWithOneOf(wkz, ['012', '014', '016', '018'])
}

function isTrike(wkz: string) {
  return startsWithOneOf(wkz, ['030'])
}

function isQuad(wkz: string) {
  return startsWithOneOf(wkz, ['011', '031'])
}

export function isEMoped(wkz: string) {
  return startsWithOneOf(wkz, ['005', '006', '008'])
}
